// Location-aware greetings + Setswana phrase rotation for the HeroBand.
// Pure functions — testable, no side effects, no network calls.

export type GreetingLocale = 'tn' | 'en' | 'hi' | 'zu';

// Time-of-day buckets, local time at user's location.
type TimeOfDay = 'morning' | 'afternoon' | 'evening' | 'night';

const timeOfDayFromHour = (hour: number): TimeOfDay => {
  if (hour < 5) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 21) return 'evening';
  return 'night';
};

const GREETINGS: Record<GreetingLocale, Record<TimeOfDay, string>> = {
  tn: {
    morning: 'Dumela',
    afternoon: 'Dumela',
    evening: 'Dumela',
    night: 'Boroko',
  },
  en: {
    morning: 'Good morning',
    afternoon: 'Good afternoon',
    evening: 'Good evening',
    night: 'Hi',
  },
  hi: {
    morning: 'Suprabhat',
    afternoon: 'Namaste',
    evening: 'Shubh sandhya',
    night: 'Shubh ratri',
  },
  zu: {
    morning: 'Sawubona',
    afternoon: 'Sawubona',
    evening: 'Sawubona',
    night: 'Lala kahle',
  },
};

// Map ISO country codes to a preferred greeting locale. Defaults to English.
const COUNTRY_TO_LOCALE: Record<string, GreetingLocale> = {
  BW: 'tn', // Botswana → Setswana
  IN: 'hi', // India → Hindi
  ZA: 'zu', // South Africa → Zulu (a bit arbitrary; could be SOT/AFR)
};

export const localeFromCountry = (countryCode: string | undefined): GreetingLocale => {
  if (!countryCode) return 'en';
  return COUNTRY_TO_LOCALE[countryCode.toUpperCase()] ?? 'en';
};

export const greetingFor = (
  firstName: string,
  hour: number,
  locale: GreetingLocale,
): string => {
  const word = GREETINGS[locale][timeOfDayFromHour(hour)];
  return `${word}, ${firstName}`;
};

// Flavour lines, keyed by weekday. So Friday line on Fridays, Monday line
// on Mondays. Two-or-three options per (locale × weekday); the day-of-year
// modulo picks which option you see this week, so the same Friday isn't
// quoted back at you every week.

type DayLine = { line: string; gloss: string };

// 0 = Sunday … 6 = Saturday (JS Date.getDay convention)
type LineMap = Record<number, DayLine[]>;

const SETSWANA: LineMap = {
  0: [
    { line: 'Ke Sunday — ikhutse mme o ipaakanyetse beke e ntšhwa.', gloss: "It's Sunday — rest and prepare for a fresh week." },
  ],
  1: [
    { line: 'Le kae? Ke Monday — a re itlhaganeleng.', gloss: "How are you? It's Monday — let's get a move on." },
    { line: 'Ke Monday! Beke e ntšhwa, ditshono di ntšhwa.', gloss: 'Monday! New week, new chances.' },
  ],
  2: [
    { line: 'Ke Tuesday — bonako, le mosola.', gloss: 'Tuesday — speed, with purpose.' },
    { line: 'Tirelo e ntle e simologa ka go nna o le mongwe.', gloss: 'Great work starts with showing up.' },
  ],
  3: [
    { line: 'Ke Wednesday — middle of the climb. 💪', gloss: '' },
    { line: 'Hump day — tota o ka kgona.', gloss: 'Hump day — you can do this.' },
  ],
  4: [
    { line: 'Ke Thursday — almost there.', gloss: '' },
    { line: 'Bua sentle, dira sentle.', gloss: 'Speak well, work well.' },
  ],
  5: [
    { line: 'Ke Friday! One more push and the weekend is yours.', gloss: '' },
    { line: 'Friday — Pula! Make today count.', gloss: '' },
  ],
  6: [
    { line: 'Ke Saturday — o bereka ka weekend? Tlhokomela leano la gago.', gloss: "Saturday — working the weekend? Mind your pace." },
  ],
};

const ENGLISH: LineMap = {
  0: [
    { line: 'Sunday — rest is part of the work.', gloss: '' },
  ],
  1: [
    { line: 'Coffee first, then conquer Monday.', gloss: '' },
    { line: 'Monday: a clean slate for the week.', gloss: '' },
  ],
  2: [
    { line: 'Tuesday — small steps still cover ground.', gloss: '' },
    { line: "Today's effort is tomorrow's tailwind.", gloss: '' },
  ],
  3: [
    { line: 'Middle of the climb — keep pushing.', gloss: '' },
    { line: "Hump day — you're halfway there.", gloss: '' },
  ],
  4: [
    { line: 'Thursday — finish what Monday started.', gloss: '' },
    { line: "Almost the weekend. Almost.", gloss: '' },
  ],
  5: [
    { line: "Friday! One more push and the weekend is yours.", gloss: '' },
    { line: "It's Friday — finish strong, log off well.", gloss: '' },
  ],
  6: [
    { line: 'Saturday — recharge well, you earned it.', gloss: '' },
  ],
};

const HINDI: LineMap = {
  0: [{ line: 'Aaj Ravivar — aaram bhi zaroori hai.', gloss: "Sunday — rest matters too." }],
  1: [{ line: 'Somvar — nayi shuruwat.', gloss: 'Monday — a new beginning.' }],
  2: [{ line: 'Mangalvar — ek kadam aage.', gloss: 'Tuesday — one step forward.' }],
  3: [{ line: 'Budhwar — aadha safar tay.', gloss: 'Wednesday — halfway through.' }],
  4: [{ line: 'Guruwar — manzil paas hai.', gloss: 'Thursday — the goal is near.' }],
  5: [{ line: 'Shukravar — aaj se shaandaar weekend.', gloss: 'Friday — kick off a great weekend.' }],
  6: [{ line: 'Shanivar — ikhuthse, ya kaam, ya khel.', gloss: 'Saturday — rest, work, or play.' }],
};

const dayOfYear = (d: Date): number => {
  const start = new Date(d.getFullYear(), 0, 0);
  return Math.floor((d.getTime() - start.getTime()) / 86400000);
};

export const flavourLineFor = (locale: GreetingLocale, date: Date): DayLine => {
  const map =
    locale === 'tn' ? SETSWANA : locale === 'hi' ? HINDI : ENGLISH;
  const options = map[date.getDay()] ?? ENGLISH[date.getDay()] ?? [];
  if (options.length === 0) {
    return { line: '', gloss: '' };
  }
  return options[dayOfYear(date) % options.length] ?? options[0]!;
};
